/**
 * Faction intel (terms version 5, owner 2026-10-26: "Yes share estimates. Share all useful info between faction mates."):
 * what a member's Omni-Torn learned about OTHER players, shared with their faction mates through the Omni-Torn server
 * (apps/api/src/faction-intel.ts), so a mate's extension draws it at once and skips its own lookups.
 *
 * Per (faction, player) the server keeps the latest of each part:
 *  - est:<source> — a stat estimate with its own date: a TornStats spy (faction only — TornStats' terms), an FF Scouter
 *    estimate (free for anyone), or the fair fight from a member's own fight (their API attack log);
 *  - revivable — whether the player has revives on, as Torn showed it (a reviver's page view, or the API's is_revivable);
 *  - chance — the revive success chance Torn shows a reviver on that player's profile / list;
 *  - med — the med watch's observations (estimated medical cooldown end, last revive, last early exit, cooldown seen
 *    above the 6-hour base cap).
 * An entry goes FACTION_INTEL_KEEP_DAYS after it was last sent. Checked the same way on both sides (only these fields,
 * sane values).
 */

import type { SpyEntry } from './spies';

/** Days an entry is kept after it was last sent. */
export const FACTION_INTEL_KEEP_DAYS = 7;
/** Players per read. */
export const FACTION_INTEL_MAX_IDS = 100;
/** Players per write. */
export const FACTION_INTEL_MAX_ENTRIES = 200;

export const INTEL_ESTIMATE_SOURCES = ['tornstats-spy', 'ffscouter', 'fights'] as const;
export type IntelEstimateSource = (typeof INTEL_ESTIMATE_SOURCES)[number];
/** How old (its own date) an estimate may be to be shared at all. */
const EST_MAX_AGE_DAYS: Record<IntelEstimateSource, number> = { 'tornstats-spy': 180, ffscouter: 30, fights: 180 };
/** How old a revive / med observation may be to be shared. */
const OBS_MAX_AGE_MS = FACTION_INTEL_KEEP_DAYS * 86_400_000;

/** Where a revive observation came from: a Torn page the sender was viewing, or Torn's API. */
export type IntelOrigin = 'page' | 'api';

export interface IntelEstimate {
  source: IntelEstimateSource;
  /** When the estimate itself is from (ms): the spy's date, FF Scouter's update, the fight's end. */
  at: number;
  /** Lowest–highest total battle stats (null top: at least). */
  total: [number, number | null];
  estimate?: number | null;
  /** Battle stat score, when that's what was measured (exact). */
  score?: number | null;
  sigma?: number | null;
  strength?: number | null;
  defense?: number | null;
  speed?: number | null;
  dexterity?: number | null;
}

export interface IntelRevivable {
  on: boolean;
  at: number;
  from: IntelOrigin;
}

export interface IntelChance {
  /** Torn's revive success chance, 0–100. */
  pct: number;
  at: number;
  from: IntelOrigin;
}

export interface IntelMed {
  /** When this was last seen (ms). */
  at: number;
  /** Estimated end of their medical cooldown (ms). */
  cdUntil?: number | null;
  /** When they were last seen revived out of hospital (ms). */
  lastRevive?: number | null;
  /** When they last left hospital early (ms), and with how many minutes left. */
  lastExit?: number | null;
  exitMins?: number | null;
  /** The most medical cooldown (minutes) seen on them before their last item — above 360 means a raised cap. */
  overCapMins?: number | null;
  /** Their faction (for the faction's cooldown cap). */
  factionId?: number | null;
}

/** One player, as sent. */
export interface IntelEntry {
  id: number;
  est?: IntelEstimate[];
  revivable?: IntelRevivable;
  chance?: IntelChance;
  med?: IntelMed;
}

/** One player, as read back. */
export interface IntelRead {
  est: Partial<Record<IntelEstimateSource, IntelEstimate>>;
  /** The newest spy of them that you or your faction recorded (spies.ts; added by the server from its spy log, never sent in). */
  spy?: SpyEntry;
  revivable?: IntelRevivable;
  chance?: IntelChance;
  med?: IntelMed;
}

type Obj = Record<string, unknown>;
const obj = (x: unknown): Obj | null => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Obj) : null);
const fin = (x: unknown, lo: number, hi: number): number | null => (typeof x === 'number' && Number.isFinite(x) && x >= lo && x <= hi ? x : null);
const STAT_MAX = 1e15;
const optStat = (x: unknown): number | null | undefined => (x === undefined || x === null ? undefined : (fin(x, 0, STAT_MAX) ?? undefined));
/** A date (ms) not over `maxAgeMs` old; up to a minute ahead counts as now. */
const when = (x: unknown, now: number, maxAgeMs: number): number | null => {
  const t = fin(x, 0, now + 60_000);
  return t === null || t < now - maxAgeMs ? null : Math.min(Math.round(t), now);
};
const origin = (x: unknown): IntelOrigin | null => (x === 'page' || x === 'api' ? x : null);

export function cleanIntelEstimate(x: unknown, now: number): IntelEstimate | null {
  const o = obj(x);
  if (!o || !(INTEL_ESTIMATE_SOURCES as readonly string[]).includes(String(o.source))) return null;
  const source = o.source as IntelEstimateSource;
  const at = when(o.at, now, EST_MAX_AGE_DAYS[source] * 86_400_000);
  const t = Array.isArray(o.total) ? o.total : null;
  const lo = fin(t?.[0], 0, STAT_MAX);
  const hi = t?.[1] === null ? null : fin(t?.[1], 0, STAT_MAX);
  if (at === null || lo === null || (t?.[1] !== null && hi === null) || (hi !== null && hi < lo) || (lo === 0 && !hi)) return null;
  const out: IntelEstimate = { source, at, total: [lo, hi] };
  const extra = { estimate: optStat(o.estimate), score: optStat(o.score), sigma: o.sigma === undefined || o.sigma === null ? undefined : (fin(o.sigma, 0, 5) ?? undefined), strength: optStat(o.strength), defense: optStat(o.defense), speed: optStat(o.speed), dexterity: optStat(o.dexterity) };
  for (const [k, v] of Object.entries(extra)) if (v !== undefined) (out as unknown as Obj)[k] = v;
  return out;
}

/** Only the known fields with sane values; null when nothing usable is left. */
export function cleanIntelEntry(x: unknown, now: number): IntelEntry | null {
  const o = obj(x);
  const id = o?.id;
  if (!o || !Number.isInteger(id) || (id as number) <= 0 || (id as number) > 1e8) return null;
  const out: IntelEntry = { id: id as number };
  if (Array.isArray(o.est)) {
    const by = new Map<IntelEstimateSource, IntelEstimate>();
    for (const e of o.est.slice(0, 6)) {
      const c = cleanIntelEstimate(e, now);
      if (c && (!by.has(c.source) || by.get(c.source)!.at < c.at)) by.set(c.source, c);
    }
    if (by.size) out.est = [...by.values()];
  }
  const rv = obj(o.revivable);
  if (rv && typeof rv.on === 'boolean' && origin(rv.from)) {
    const at = when(rv.at, now, OBS_MAX_AGE_MS);
    if (at !== null) out.revivable = { on: rv.on, at, from: origin(rv.from)! };
  }
  const ch = obj(o.chance);
  if (ch && origin(ch.from)) {
    const pct = fin(ch.pct, 0, 100);
    const at = when(ch.at, now, OBS_MAX_AGE_MS);
    if (pct !== null && at !== null) out.chance = { pct: Math.round(pct * 100) / 100, at, from: origin(ch.from)! };
  }
  const md = obj(o.med);
  if (md) {
    const at = when(md.at, now, OBS_MAX_AGE_MS);
    if (at !== null) {
      const med: IntelMed = { at };
      const cd = fin(md.cdUntil, now - 86_400_000, now + 12 * 3_600_000);
      if (cd !== null) med.cdUntil = Math.round(cd);
      const rev = when(md.lastRevive, now, OBS_MAX_AGE_MS);
      if (rev !== null) med.lastRevive = rev;
      const exit = when(md.lastExit, now, OBS_MAX_AGE_MS);
      if (exit !== null) med.lastExit = exit;
      const mins = fin(md.exitMins, 0, 100_000);
      if (mins !== null) med.exitMins = Math.round(mins);
      const over = fin(md.overCapMins, 0, 24 * 60);
      if (over !== null) med.overCapMins = Math.round(over);
      const f = md.factionId;
      if (Number.isInteger(f) && (f as number) > 0 && (f as number) < 1e8) med.factionId = f as number;
      if (Object.keys(med).length > 1) out.med = med;
    }
  }
  return out.est || out.revivable || out.chance || out.med ? out : null;
}
